import React from "react";
import { Star, Leaf } from "lucide-react";

/**
 * Compact rating chip for shop cards. Two variants:
 *   • `google` (default) — Google Places star rating (amber, star icon)
 *   • `native` — Bud Link community rating (emerald, leaf icon)
 *
 * Renders nothing when there's no data yet, so cards without reviews stay
 * clean instead of showing empty stars.
 */
export const ShopRating = ({
  rating,
  count,
  slug,
  variant = "google",
  className = "",
}) => {
  if (!rating || !count) return null;
  const rounded = Math.round(rating * 10) / 10;
  const isNative = variant === "native";
  const Icon = isNative ? Leaf : Star;
  const tone = isNative
    ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-200"
    : "bg-amber-500/15 border-amber-500/30 text-amber-200";
  const iconTone = isNative
    ? "fill-emerald-300 text-emerald-300"
    : "fill-amber-300 text-amber-300";
  const meta = isNative
    ? `Bud Link community · ${rounded} from ${count.toLocaleString()} review${count === 1 ? "" : "s"}`
    : `Google · ${rounded} from ${count.toLocaleString()} reviews`;
  const testId = slug ? `shop-rating-${variant}-${slug}` : `shop-rating-${variant}`;
  const label = isNative ? "Community" : "Google";
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${tone} ${className}`}
      title={meta}
      aria-label={meta}
    >
      <Icon className={`w-3 h-3 ${iconTone}`} strokeWidth={0} />
      <span className="tabular-nums">{rounded.toFixed(1)}</span>
      <span className="opacity-70">
        ({count > 999 ? `${(count / 1000).toFixed(1)}k` : count})
      </span>
      <span className="hidden sm:inline opacity-60 text-[9px] uppercase tracking-wider ml-0.5">
        {label}
      </span>
    </span>
  );
};

export default ShopRating;
